/**
 * ⭐ P6.19 解锁申请列表。
 *
 * 数据源：`GET /api/freeze/unlock-requests`（服务端按权限过滤：
 *   超管 / 持 approval:approve 见数据范围内申请；其余仅见本人发起）。
 *
 * 批准 / 拒绝：
 *   - 有 `freeze:unlock_request` 权限者（如 HQ_ADMIN）可直接操作；
 *   - 拒绝理由必填且不少于 20 字（解冻模板 min_reason_length）。
 */

import { useState } from 'react';
import EmptyState from '../../components/EmptyState';
import Spinner from '../../components/Spinner';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import { ApiError } from '../../api/client';
import UnlockRejectDialog from './UnlockRejectDialog';
import {
  useUnlockRequests,
  useApproveUnlock,
  type UnlockRequestItem,
  type UnlockRequestStatus,
} from './useApprovals';

function fmtTime(iso: string): string {
  return iso.slice(0, 16).replace('T', ' ');
}

const STATUS_TONE: Record<UnlockRequestStatus, 'ok' | 'warn' | 'alert' | 'idle'> = {
  PENDING: 'warn',
  APPROVED: 'ok',
  REJECTED: 'alert',
  EXPIRED: 'alert',
  USED: 'idle',
};

const STATUS_LABEL: Record<UnlockRequestStatus, string> = {
  PENDING: '待审批',
  APPROVED: '已批准',
  REJECTED: '已拒绝',
  EXPIRED: '已超时',
  USED: '已使用',
};

/** SLA 剩余文案；无 SLA 返回 null；已超时返回「已超 SLA xh」 */
function slaText(item: UnlockRequestItem): { text: string; over: boolean } | null {
  const h = item.slaRemainHours;
  if (h == null) return null;
  if (h <= 0) return { text: `已超 SLA ${Math.abs(Math.round(h))}h`, over: true };
  return { text: `剩余 ${h.toFixed(1)}h`, over: false };
}

function friendlyApproveError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'UNLOCK_ALREADY_HANDLED') return '该解冻申请已被处理，请刷新';
    if (err.code === 'UNLOCK_REQUEST_NOT_FOUND') return '该解冻申请不存在或已被删除';
    if (err.code === 'AUTH_PERMISSION_DENIED') return '你没有批准解冻申请的权限';
    if (err.code === 'UNLOCK_SELF_APPROVAL_NOT_ALLOWED')
      return '你是该解冻申请的申请人，不能批准自己的申请，需由他人复核';
    if (err.code === 'UNLOCK_NOT_ASSIGNED_APPROVER')
      return '当前解冻节点由省管理员审批，你的角色不在审批范围内';
    return err.message;
  }
  return err instanceof Error ? err.message : '操作失败';
}

export default function UnlockRequestList() {
  const { data, isLoading, isError, error } = useUnlockRequests();
  const approve = useApproveUnlock();
  const [rejectItem, setRejectItem] = useState<UnlockRequestItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (isLoading) return <Spinner size="sm" label="加载解锁申请…" />;

  if (isError) {
    return (
      <EmptyState
        icon="⚠"
        title="解锁申请加载失败"
        hint={error?.message ?? '请稍后重试'}
      />
    );
  }

  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon="🔓"
        title="暂无待处理的解锁申请"
        hint="任务被冻结后申请人提交解冻申请，会显示在这里等待审批"
      />
    );
  }

  const onApprove = (item: UnlockRequestItem) => {
    setActionError(null);
    approve.mutate(
      { id: item.id },
      { onError: (err) => setActionError(friendlyApproveError(err)) },
    );
  };

  return (
    <div className="unlock-list">
      {actionError && <div className="cf-error">⚠ {actionError}</div>}

      {data.map((item) => {
        const sla = slaText(item);
        const pending = item.status === 'PENDING';
        return (
          <div
            className={['unlock-row', pending ? 'unlock-row--pending' : ''].join(' ')}
            key={item.id}
          >
            <div className="unlock-row__head">
              <Badge tone="frozen">🔓 解冻申请</Badge>
              <Badge tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</Badge>
              <span className="unlock-row__task">
                {item.taskName ?? `任务 #${item.taskId ?? '—'}`}
                {item.taskCode ? ` · 编号 ${item.taskCode}` : ''}
              </span>
              <span className="unlock-row__at num">{fmtTime(item.createdAt)}</span>
            </div>

            <div className="unlock-row__meta">
              <span>项目：{item.projectName ?? '—'}</span>
              <span>申请人：{item.applicantName ?? '—'}</span>
              {item.currentNodeName && (
                <span>
                  当前节点：{item.currentNodeName}（第 {item.currentNodeSeq} 级）
                </span>
              )}
              {sla && (
                <span className={['unlock-row__sla', sla.over ? 'is-over' : ''].join(' ')}>
                  SLA：{sla.text}
                </span>
              )}
            </div>

            {item.reason && (
              <div className="unlock-row__reason">解冻原因：{item.reason}</div>
            )}

            {pending && item.canIApprove && (
              <div className="unlock-row__btns">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={approve.isPending}
                  onClick={() => onApprove(item)}
                >
                  批准
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setRejectItem(item)}
                >
                  拒绝
                </Button>
              </div>
            )}
          </div>
        );
      })}

      <UnlockRejectDialog
        item={rejectItem}
        open={rejectItem !== null}
        onClose={() => setRejectItem(null)}
      />
    </div>
  );
}
